import { FEEDBACK_COLORS, FEEDBACK_TOOLS } from './feedbackTypes.js';

// Prototype-exact tool icons (stroke SVGs from the approved hi-fi prototype).
const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const TOOL_ICONS = {
  select: (
    <svg {...STROKE_PROPS}>
      <path d="M5 3l14 8-6.5 1.5L9 19 5 3z" />
    </svg>
  ),
  rectangle: (
    <svg {...STROKE_PROPS}>
      <rect x="4" y="5" width="16" height="14" rx="2" />
    </svg>
  ),
  arrow: (
    <svg {...STROKE_PROPS}>
      <path d="M5 19L19 5" />
      <path d="M10 5h9v9" />
    </svg>
  ),
  highlight: (
    <svg {...STROKE_PROPS}>
      <path d="M9 11l6-6 4 4-6 6H9v-4z" />
      <path d="M9 15l-3 3H3l3-4" />
      <path d="M3 21h18" />
    </svg>
  ),
  pin: (
    <svg {...STROKE_PROPS}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  text: (
    <svg {...STROKE_PROPS}>
      <path d="M5 5h14M12 5v14" />
    </svg>
  ),
  pen: (
    <svg {...STROKE_PROPS}>
      <path d="M17 3l4 4L8 20l-5 1 1-5L17 3z" />
    </svg>
  ),
};

const ACTION_ICONS = {
  undo: (
    <svg {...STROKE_PROPS}>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
    </svg>
  ),
  redo: (
    <svg {...STROKE_PROPS}>
      <path d="M15 14l5-5-5-5" />
      <path d="M20 9H10a6 6 0 0 0 0 12h3" />
    </svg>
  ),
  trash: (
    <svg {...STROKE_PROPS}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </svg>
  ),
};

// Short pill labels (prototype: PIN · BOX · ARROW · HIGHLIGHT · PEN).
const TOOL_SHORT_LABEL = {
  select: 'Select',
  rectangle: 'Box',
  arrow: 'Arrow',
  highlight: 'Highlight',
  pin: 'Pin',
  text: 'Text',
  pen: 'Pen',
};

export function MarkupToolbar({
  activeTool,
  activeColor = 'yellow',
  onToolChange,
  onColorChange,
  onDelete,
  onUndo,
  onRedo,
  canDelete,
}) {
  return (
    <div className="feedback-toolbar" role="toolbar" aria-label="Markup tools">
      {FEEDBACK_TOOLS.map((tool) => (
        <button
          key={tool.id}
          type="button"
          className={`fbws-tool ${activeTool === tool.id ? 'on' : ''}`}
          data-testid={tool.testId}
          aria-label={tool.label}
          aria-pressed={activeTool === tool.id}
          title={tool.label}
          onClick={() => onToolChange?.(tool.id)}
        >
          {TOOL_ICONS[tool.id] || <span aria-hidden>{tool.glyph}</span>}
          <span className="fbws-tool-lbl" aria-hidden>
            {TOOL_SHORT_LABEL[tool.id] || tool.label}
          </span>
        </button>
      ))}
      <span className="fbws-tb-sep" aria-hidden />
      <div className="fbws-swatches" aria-label="Annotation colors">
        {FEEDBACK_COLORS.map((color) => (
          <button
            key={color.id}
            type="button"
            className={`feedback-swatch ${activeColor === color.id ? 'active' : ''}`}
            style={{ '--swatch': color.value }}
            aria-label={color.label}
            aria-pressed={activeColor === color.id}
            title={color.label}
            onClick={() => onColorChange?.(color.id)}
          />
        ))}
      </div>
      <span className="fbws-tb-sep" aria-hidden />
      <button type="button" className="fbws-tool" aria-label="Undo" title="Undo" onClick={onUndo}>
        {ACTION_ICONS.undo}
        <span className="fbws-tool-lbl" aria-hidden>
          Undo
        </span>
      </button>
      <button type="button" className="fbws-tool" aria-label="Redo" title="Redo" onClick={onRedo}>
        {ACTION_ICONS.redo}
        <span className="fbws-tool-lbl" aria-hidden>
          Redo
        </span>
      </button>
      <button
        type="button"
        className="fbws-tool"
        aria-label="Delete annotation"
        title="Delete selected mark"
        onClick={onDelete}
        disabled={!canDelete}
      >
        {ACTION_ICONS.trash}
        <span className="fbws-tool-lbl" aria-hidden>
          Delete
        </span>
      </button>
    </div>
  );
}
